/*
 * Cart drawer
 * - Opens a slide-out cart instead of sending shoppers to /cart.
 * - Exposes the same API as <cart-notification> (setActiveElement, getSectionsToRender,
 *   renderContents) so product-form.js can use either one.
 * - Catches native /cart/add form submits (including ones triggered by product option
 *   apps via form.submit()) and turns them into AJAX adds.
 * - Refreshes when other scripts change the cart through the AJAX API.
 */
if (!customElements.get('cart-drawer')) {
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;
  const nativeFetch = window.fetch.bind(window);

  const getCartDrawer = () => document.querySelector('cart-drawer');

  const isCartAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute('action') || '';
    return CART_ADD_PATTERN.test(action.split('#')[0]);
  };

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && input.url) return input.url;
    return '';
  };

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.panel = this.querySelector('#CartDrawer');
      this.notice = this.querySelector('.cart-drawer__notice');
      this.onCartPage = this.dataset.onCartPage === 'true';
      this.onKeyUp = (evt) => evt.code === 'Escape' && this.close();
      this.debouncedQuantityChange = debounce((input) => this.onQuantityInput(input), 400);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', (evt) => {
        if (evt.target.matches('[data-cart-drawer-qty-input]')) this.debouncedQuantityChange(evt.target);
      });
    }

    connectedCallback() {
      // Fixed positioning breaks inside the transformed sticky header, so live on <body>.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('body > cart-drawer').forEach((drawer) => drawer !== this && drawer.remove());
        document.body.appendChild(this);
      }

      const icon = document.getElementById('cart-icon-bubble');
      if (icon && !this.onCartPage) {
        icon.setAttribute('aria-haspopup', 'dialog');
        icon.setAttribute('aria-controls', 'CartDrawer');
      }
    }

    onClick(evt) {
      if (evt.target.closest('[data-cart-drawer-close]')) {
        evt.preventDefault();
        this.close();
        return;
      }

      const qtyButton = evt.target.closest('[data-cart-drawer-qty]');
      if (qtyButton) {
        const input = qtyButton.closest('.cart-drawer-quantity').querySelector('input');
        const current = parseInt(input.value, 10) || 0;
        input.value = qtyButton.dataset.cartDrawerQty === 'plus' ? current + 1 : Math.max(current - 1, 0);
        this.debouncedQuantityChange(input);
        return;
      }

      const removeButton = evt.target.closest('[data-cart-drawer-remove]');
      if (removeButton) {
        const item = removeButton.closest('.cart-drawer-item');
        this.updateLine(item.dataset.key, 0, item);
      }
    }

    onQuantityInput(input) {
      const item = input.closest('.cart-drawer-item');
      if (!item) return;
      const quantity = Math.max(parseInt(input.value, 10) || 0, 0);
      this.updateLine(item.dataset.key, quantity, item);
    }

    open(opener) {
      if (opener) this.setActiveElement(opener);
      const header = document.querySelector('sticky-header');
      if (header && header.reveal) header.reveal();

      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keyup', this.onKeyUp);

      const focusPanel = () => trapFocus(this.panel);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        focusPanel();
      } else {
        this.panel.addEventListener('transitionend', focusPanel, { once: true });
      }
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keyup', this.onKeyUp);
      this.hideNotice();
      removeTrapFocus(this.activeElement);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer' },
        { id: 'cart-icon-bubble' }
      ];
    }

    // Called by product-form.js with the /cart/add response.
    renderContents(parsedState) {
      this.hideNotice();
      this.renderSections(parsedState.sections);
      this.open();
    }

    renderSections(sections) {
      if (!sections) return;

      if (sections['cart-drawer']) {
        this.querySelector('#CartDrawer-Inner').innerHTML =
          this.getSectionInnerHTML(sections['cart-drawer']);
        this.classList.toggle('is-empty', !!this.querySelector('.cart-drawer__empty'));
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble']);
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return element ? element.innerHTML : '';
    }

    sectionIds() {
      return this.getSectionsToRender().map((section) => section.id);
    }

    refresh() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return nativeFetch(`${root}?sections=${this.sectionIds().join(',')}`)
        .then((response) => response.json())
        .then((sections) => this.renderSections(sections))
        .catch((e) => console.error(e));
    }

    updateLine(key, quantity, item) {
      if (item) item.classList.add('is-loading');
      this.classList.add('is-busy');

      const body = JSON.stringify({
        id: key,
        quantity,
        sections: this.sectionIds(),
        sections_url: window.location.pathname
      });

      nativeFetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((state) => {
          if (state.status || state.errors) {
            this.showItemError(item, state.description || state.message || state.errors || window.cartStrings.error);
            return this.refresh();
          }
          this.renderSections(state.sections);
          this.dispatchCartUpdated();
        })
        .catch(() => this.showItemError(item, window.cartStrings.error))
        .finally(() => {
          this.classList.remove('is-busy');
          const refreshedItem = this.querySelector(`[data-key="${key}"]`);
          if (refreshedItem) refreshedItem.classList.remove('is-loading');
        });
    }

    // Adds a native /cart/add form via AJAX, then opens the drawer.
    addFromForm(form, submitter) {
      if (this.adding) return;
      this.adding = true;

      const button = submitter || form.querySelector('[type="submit"]');
      if (button) {
        button.setAttribute('aria-disabled', true);
        button.classList.add('loading');
      }
      this.setActiveElement(button || document.activeElement);

      const formData = new FormData(form);
      if (submitter && submitter.name && !formData.has(submitter.name)) formData.append(submitter.name, submitter.value);
      formData.append('sections', this.sectionIds());
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      nativeFetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showNotice(response.description || response.message || window.cartStrings.error);
            this.open();
            return;
          }
          this.renderContents(response);
          this.dispatchCartUpdated();
        })
        .catch((e) => {
          console.error(e);
          this.showNotice(window.cartStrings.error);
          this.open();
        })
        .finally(() => {
          this.adding = false;
          if (button) {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          }
        });
    }

    showItemError(item, message) {
      const target = item && this.querySelector(`[data-key="${item.dataset.key}"] .cart-drawer-item__error`);
      if (target) {
        target.textContent = message;
        target.hidden = false;
      } else {
        this.showNotice(message);
      }
    }

    showNotice(message) {
      if (!this.notice) return;
      this.notice.textContent = message;
      this.notice.hidden = false;
    }

    hideNotice() {
      if (this.notice) this.notice.hidden = true;
    }

    dispatchCartUpdated() {
      document.dispatchEvent(new CustomEvent('cart-drawer:updated', { bubbles: true }));
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  // Header cart icon opens the drawer.
  document.addEventListener('click', (evt) => {
    const icon = evt.target.closest('#cart-icon-bubble');
    const drawer = getCartDrawer();
    if (!icon || !drawer || drawer.onCartPage) return;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button === 1) return;
    evt.preventDefault();
    drawer.open(icon);
  });

  // Native add-to-cart form submits (forms not handled by <product-form>).
  document.addEventListener('submit', (evt) => {
    const drawer = getCartDrawer();
    if (evt.defaultPrevented || !drawer || drawer.onCartPage || !isCartAddForm(evt.target)) return;
    evt.preventDefault();
    drawer.addFromForm(evt.target, evt.submitter);
  });

  // Apps that call form.submit() skip the submit event, so route those through the drawer too.
  const nativeSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    const drawer = getCartDrawer();
    if (drawer && !drawer.onCartPage && isCartAddForm(this)) {
      drawer.addFromForm(this);
      return;
    }
    return nativeSubmit.call(this);
  };

  // Keep the drawer in sync when other scripts change the cart via the AJAX API.
  const onExternalCartChange = debounce((url) => {
    const drawer = getCartDrawer();
    if (!drawer || drawer.onCartPage) return;
    drawer.refresh().then(() => {
      if (CART_ADD_PATTERN.test(url)) drawer.open();
    });
  }, 300);

  window.fetch = function (input, init) {
    const promise = nativeFetch(input, init);
    const url = requestUrl(input);
    if (CART_MUTATION_PATTERN.test(url)) {
      promise.then((response) => response.ok && onExternalCartChange(url)).catch(() => {});
    }
    return promise;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._cartDrawerUrl = String(url);
    return nativeOpen.apply(this, arguments);
  };

  const nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    const url = this._cartDrawerUrl || '';
    if (CART_MUTATION_PATTERN.test(url)) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalCartChange(url);
      });
    }
    return nativeSend.apply(this, arguments);
  };
}
